import React, { useCallback } from 'react';
import { Dropdown } from '@cuemath/web-comps';

import Pagination from 'modules/list/pagination';
import Search from 'modules/list/new-search-bar';

import ReviewPanel from './review-panel';
import QueueTableRow from './queue-row';
import {
  DEFAULT_QUEUE_KEY,
  PAGE_SIZE_OPTIONS,
  QUEUES,
  SEARCH_FETCH_LIMIT,
} from './constants';
import { sub } from './row-fields';
import useFeedback from './hooks/use-feedback';
import useHoFQueue from './hooks/use-hof-queue';
import useExamLibrary from './hooks/use-exam-library';
import useRecordEditor from './hooks/use-record-editor';
import useRecordActions from './hooks/use-record-actions';
import useRecordTriage from './hooks/use-record-triage';
import useRecordSupply from './hooks/use-record-supply';
import {
  Btn,
  EmptyGlyph,
  EmptyMark,
  EmptyState,
  EmptyTitle,
  HeadActions,
  Muted,
  PageHead,
  PageSub,
  PageTitle,
  Pager,
  PagerText,
  Panel,
  PanelBar,
  PanelTitle,
  Root,
  Row,
  RowsPicker,
  Table,
  TableWrap,
  ThLabel,
  Tile,
  TileCount,
  TileHint,
  TileLabel,
  Tiles,
} from './styled-comps';

const HallOfFame = () => {
  const { toast, confirmThen, copyValue, reportOutcome } = useFeedback();
  // Before the queue, not after: a search matches on the exam *name*, and only the library
  // knows it — the row carries a key.
  const { examNames, examScoreTypes } = useExamLibrary();
  const queue = useHoFQueue({ examNames });
  const editor = useRecordEditor({
    toast,
    confirmThen,
    reportOutcome,
    refresh: queue.refresh,
  });
  const actions = useRecordActions({
    toast,
    confirmThen,
    reportOutcome,
    refresh: queue.refresh,
    closeEditor: editor.close,
  });
  const triage = useRecordTriage({
    row: editor.editing,
    reportOutcome,
    refresh: queue.refresh,
    closeEditor: editor.close,
  });
  const text = useRecordSupply({
    row: editor.editing,
    // The only `scoreType` the endpoint accepts, so the drawer cannot offer a settlement
    // the service would refuse.
    // `sub` dereferences, and the hook runs with the drawer closed, so the row is null
    // most of the time this line is evaluated.
    libraryScoreType: examScoreTypes[sub(editor.editing || {}).examKey],
    refresh: queue.refresh,
    reportOutcome,
  });

  // Stable, so the memoized rows do not re-render on every parent render.
  const copyStudentId = useCallback(value => copyValue('Student ID', value), [copyValue]);

  return (
    <Root>
      <PageHead>
        <div>
          <PageTitle>Hall of Fame</PageTitle>
          <PageSub>Student achievements published to cuemath.com</PageSub>
        </div>
        <HeadActions>
          <Btn disabled={actions.assembling} onClick={actions.runAgent}>
            {actions.assembling ? 'Running…' : 'Run assemble agent'}
          </Btn>
        </HeadActions>
      </PageHead>

      {/* The queue is the filter: the counts are the only view of how much work is
          waiting, and clicking a tile is how you move between the queues. */}
      <Tiles>
        {QUEUES.map((q, i) => {
          const count = queue.counts[q.key];
          const unknown = count === null || count === undefined;
          return (
            <Tile
              key={q.key}
              tone={q.tone}
              index={i}
              active={queue.activeKey === q.key}
              aria-pressed={queue.activeKey === q.key}
              onClick={() => queue.changeQueue(q.key)}
            >
              <TileCount unknown={unknown}>{unknown ? '—' : count}</TileCount>
              <TileLabel>{q.label}</TileLabel>
              <TileHint>{q.hint}</TileHint>
            </Tile>
          );
        })}
      </Tiles>

      <Panel>
        <PanelBar>
          <PanelTitle>
            {queue.heading}
            {queue.total ? ` · ${queue.total}` : ''}
          </PanelTitle>
          <Row>
            {/* Matches among the rows that came back, not among all of them. */}
            {queue.isTruncated && (
              <Muted>Searching the {SEARCH_FETCH_LIMIT} most recent records only</Muted>
            )}
            {queue.isRefreshing && <Muted>Refreshing…</Muted>}
            {/* Controlled via `customSearchData`: the tiles clear the search, and an
                uncontrolled box would keep showing a term no longer applied. It also
                filters as you type rather than on Enter. */}
            <Search
              showNewSearchBar
              width="240px"
              placeholder="Search name, exam or record id"
              customSearchData={{
                searchValue: queue.search,
                onChange: queue.changeSearch,
              }}
              onSearch={queue.changeSearch}
              showClearIcon={!!queue.search}
            />
            {queue.isFiltered && (
              <Btn small onClick={queue.showAllRecords}>
                View all records
              </Btn>
            )}
            <Btn small onClick={queue.refresh}>
              Refresh
            </Btn>
          </Row>
        </PanelBar>

        {/* Before the loading check and the empty states: a failed fetch leaves no rows,
            and "Nothing matches Priya" states a fact about the student, not the request. */}
        {queue.error ? (
          <EmptyState>
            <EmptyTitle>Could not load records</EmptyTitle>
            <Muted>{queue.error}</Muted>
            <Btn small onClick={queue.refresh}>
              Try again
            </Btn>
          </EmptyState>
        ) : queue.isFirstLoad ? (
          <EmptyState>
            <Muted>Loading…</Muted>
          </EmptyState>
        ) : (
          <TableWrap busy={queue.isBusy}>
            <Table>
              <thead>
                <tr>
                  <th>
                    <ThLabel>Student</ThLabel>
                  </th>
                  <th>
                    <ThLabel>Achievement</ThLabel>
                  </th>
                  <th>
                    <ThLabel>Status</ThLabel>
                  </th>
                  <th>
                    <ThLabel>Consent</ThLabel>
                  </th>
                  <th>
                    <ThLabel>Logged</ThLabel>
                  </th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {queue.records.map(r => (
                  <QueueTableRow
                    key={r.id}
                    row={r}
                    examNames={examNames}
                    onOpen={editor.open}
                    onPublish={actions.askPublish}
                    onCopyStudentId={copyStudentId}
                  />
                ))}
                {!queue.records.length && (
                  <tr>
                    <td colSpan={6}>
                      {/* No tick on a failed lookup: the mark means a queue is clear. */}
                      {queue.isSearching ? (
                        <EmptyState>
                          <EmptyTitle>Nothing matches “{queue.search.trim()}”</EmptyTitle>
                          <Muted>
                            Search reads the student name, the achievement and the record
                            id, across every status.
                          </Muted>
                        </EmptyState>
                      ) : (
                        <EmptyState>
                          <EmptyMark aria-hidden="true">
                            <EmptyGlyph>&#10003;</EmptyGlyph>
                          </EmptyMark>
                          <EmptyTitle>
                            Nothing in {queue.heading.toLowerCase()}
                          </EmptyTitle>
                          <Muted>
                            {queue.queueKey === DEFAULT_QUEUE_KEY
                              ? 'Nothing is waiting on a decision or a fix. Pick another tile to see the rest.'
                              : 'Nothing to show for this filter.'}
                          </Muted>
                        </EmptyState>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TableWrap>
        )}

        {/* Not under an error: `isFirstLoad` is false on a failed fetch — data is undefined
            but `isProcessingFailed` is true — so without this the pager prints "No records"
            and a rows-per-page dropdown beneath "Could not load records", saying the table
            is empty when the truth is that nobody managed to ask. */}
        {!queue.error && !queue.isFirstLoad && (
          <Pager>
            <PagerText>
              {queue.total === 0
                ? // "No records" under a search would disagree with the empty state above
                  // it about which thing is empty — the table, or the search.
                  queue.isSearching
                  ? 'No matches'
                  : 'No records'
                : `Showing ${queue.firstOnPage}–${queue.lastOnPage} of ${queue.total}`}
            </PagerText>
            <Row>
              {queue.lastPage > 1 && (
                <Pagination
                  page={queue.page}
                  perPage={queue.pageSize}
                  count={queue.total}
                  onPageChange={queue.setPage}
                  shouldShowNewUi
                />
              )}
              {/* Dropdown carries its own label, so it names the control without the
                  separate span a native select needed. It also destructures a fixed prop
                  list, so aria-label would be dropped rather than forwarded. */}
              <RowsPicker>
                <Dropdown
                  type="primary"
                  label="Rows"
                  width="100%"
                  optionsWidth="76px"
                  height="max-content"
                  value={String(queue.pageSize)}
                  options={PAGE_SIZE_OPTIONS}
                  onChange={queue.changePageSize}
                  restrictSearch
                  disabledInput
                />
              </RowsPicker>
            </Row>
          </Pager>
        )}
      </Panel>

      {editor.editing && (
        <ReviewPanel
          row={editor.editing}
          examName={examNames[sub(editor.editing).examKey]}
          isDirty={editor.isDirty}
          saving={editor.saving}
          uploadingPhoto={editor.uploadingPhoto}
          photoError={editor.photoError}
          onFieldChange={editor.setField}
          onNarrativeChange={editor.setNarrativeParagraph}
          onPickPhoto={editor.pickPhoto}
          onSave={editor.save}
          onPublish={() => actions.askPublish(editor.editing)}
          onUnpublish={() => actions.askUnpublish(editor.editing)}
          onRemove={() => actions.askRemove(editor.editing)}
          onClose={editor.requestClose}
          triage={triage}
          text={text}
          hasCard={editor.hasCard}
        />
      )}
    </Root>
  );
};

export default HallOfFame;
